import { FC, useEffect } from 'react'
import { Box, Button, H1, H3, makeStyles, Typography } from '@perk-ui/core'
import { CheckCircle, LogOut, Mail } from 'react-feather'

import { useAuth } from '../../../features/auth/AuthContext'

const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: '20px',
    padding: theme.spacing(4),
    width: '100%',
    maxWidth: 520, // base (sm)
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
    margin: '0 auto',
    [theme.breakpoints.up('md')]: {
      maxWidth: 600, // medium laptops
      padding: theme.spacing(4.5),
    },
    [theme.breakpoints.up('lg')]: {
      maxWidth: 720, // 1440px desktop sweet spot
      padding: theme.spacing(5),
    },
  },
  contentBox: {
    textAlign: 'center',
    maxWidth: '600px',
    width: '100%',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    backgroundColor: '#6DF5C4', // Green background for pending state
    margin: '0 auto',
    marginBottom: theme.spacing(3),
  },
  pendingIcon: {
    color: '#37b385ff', // Green color for pending
    width: '60px',
    height: '60px',
  },
  title: {
    fontSize: '36px',
    fontWeight: 700,
    color: '#212121',
    marginBottom: theme.spacing(2),
  },
  subtitle: {
    fontSize: '18px',
    fontWeight: 400,
    color: '#6B7280',
    lineHeight: '26px',
    marginBottom: theme.spacing(4),
  },
  description: {
    fontSize: '16px',
    color: '#6B7280',
    lineHeight: '24px',
    marginBottom: theme.spacing(4),
  },
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  primaryButton: {
    height: '48px',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: 600,
    textTransform: 'none',
    padding: theme.spacing(0, 4),
  },
  secondaryButton: {
    height: '48px',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: 600,
    textTransform: 'none',
    padding: theme.spacing(0, 4),
    border: `2px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      color: '#FFFFFF',
    },
  },
  highlightBox: {
    backgroundColor: '#6DF5C4',
    border: '1px solid #37b385ff',
    borderRadius: '8px',
    padding: theme.spacing(2),
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  highlightText: {
    fontSize: '14px',
    color: '#177150ff',
    fontWeight: 500,
    lineHeight: '20px',
  },
}))

const PendingApproval: FC = () => {
  const classes = useStyles()
  const { signOut } = useAuth()

  // Clear provider token from localStorage on completion
  useEffect(() => {
    localStorage.removeItem('providerImpactToken')
  }, [])

  const handleContactSupport = () => {
    window.location.href =
      'mailto:support@databiologics.com?subject=Pending Approval - Provider Access Request'
  }

  const handleSignOut = () => {
    signOut()
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.contentBox}>
        <Box className={classes.iconContainer}>
          <CheckCircle className={classes.pendingIcon} />
        </Box>

        <H1 className={classes.title}>Request Submitted</H1>

        <H3 className={classes.subtitle}>
          Your request to join this clinic has been submitted and is pending
          approval from the clinic administrator.
        </H3>

        <Typography className={classes.description}>
          We&apos;ve notified the clinic about your request. You&apos;ll receive
          an email notification once your access has been approved and you can
          begin tracking patient outcomes.
        </Typography>

        <Box className={classes.highlightBox}>
          <Typography className={classes.highlightText}>
            <strong>Important:</strong> Your account access is currently limited
            until approved by the clinic administrator. You will not be able to
            access the full platform until approval is granted.
          </Typography>
        </Box>

        <Box className={classes.buttonContainer}>
          <Button
            variant="outlined"
            className={classes.secondaryButton}
            startIcon={<Mail size={18} />}
            onClick={handleContactSupport}
          >
            Contact Support
          </Button>
          <Button
            variant="outlined"
            className={classes.secondaryButton}
            startIcon={<LogOut size={18} />}
            onClick={handleSignOut}
          >
            Sign Out
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default PendingApproval
